import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { UserPlus, Edit, ArrowLeft, Search, User, Users, FileText, CheckCircle, BarChart3, DollarSign, Link, Clock, Shield, Lock, Eye, EyeOff, Ghost, Trash2, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GhostProtocolModal } from '../../components/GhostProtocolModal';
import { useAuth } from '../../contexts/AuthContext';
import { FEATURE_PERMISSIONS, FEATURE_PERMISSION_LABELS, getDefaultFeaturePermissions } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const UsersSettings = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Check if current user is super admin (only they can grant Ghost Protocol access)
  const isSuperAdmin = currentUser?.role === 'super_admin' || currentUser?.email === 'rmarch@rhinoshieldflorida.com';
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'sales',
    phone: '',
    department: '',
    team_ids: [],
    market_ids: [],
    management_roles: [],
    ghost_protocol_access: false,
    ghost_protocol_pin: '',
    feature_permissions: {}
  });
  const [showGhostPin, setShowGhostPin] = useState(false);
  
  // Management role options (departments a user can manage)
  const MANAGEMENT_ROLE_OPTIONS = [
    { value: 'sales_manager', label: 'Sales Manager' },
    { value: 'operations_manager', label: 'Operations Manager' },
    { value: 'marketing_manager', label: 'Marketing Manager' },
    { value: 'call_center_manager', label: 'Call Center Manager' },
  ];
  
  // Master password for Ghost Protocol protection (Ryan March only)
  const MASTER_PASSWORD = '050378';
  
  const [teams, setTeams] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Ghost Protocol Modal states
  const [ghostModalOpen, setGhostModalOpen] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [ghostModalConfig, setGhostModalConfig] = useState({
    title: 'Access Control Required',
    description: 'Enter your security password to access this profile.'
  });
  
  // Filter states
  const [filterMarket, setFilterMarket] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch each resource independently to handle partial failures
      const results = await Promise.allSettled([
        axios.get(`${API}/users`),
        axios.get(`${API}/teams`),
        axios.get(`${API}/markets`),
        axios.get(`${API}/roles`),
        axios.get(`${API}/departments`)
      ]);
      
      const [usersRes, teamsRes, marketsRes, rolesRes, departmentsRes] = results;
      
      if (usersRes.status === 'fulfilled') {
        setUsers(usersRes.value.data);
      } else {
        console.error('Failed to fetch users:', usersRes.reason);
      }
      
      if (teamsRes.status === 'fulfilled') {
        setTeams(teamsRes.value.data);
      } else {
        console.error('Failed to fetch teams:', teamsRes.reason);
      }
      
      if (marketsRes.status === 'fulfilled') {
        setMarkets(marketsRes.value.data);
      } else {
        console.error('Failed to fetch markets:', marketsRes.reason);
      }
      
      if (departmentsRes.status === 'fulfilled') {
        setDepartments(departmentsRes.value.data);
      } else {
        console.error('Failed to fetch departments:', departmentsRes.reason);
      }
      
      // Format roles for dropdown
      if (rolesRes.status === 'fulfilled') {
        const formattedRoles = rolesRes.value.data
          .filter(role => role.is_active)
          .map(role => ({
            value: role.name,
            label: role.display_name || role.name
          }));
        setRoles(formattedRoles);
        console.log('Roles loaded:', formattedRoles.length);
      } else {
        console.error('Failed to fetch roles:', rolesRes.reason);
        toast.error('Failed to load roles');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, formData);
      toast.success('User created successfully');
      setCreateDialogOpen(false);
      setFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'sales',
        phone: '',
        department: '',
        team_ids: [],
        market_ids: [],
        management_roles: [],
        ghost_protocol_access: false,
        ghost_protocol_pin: '',
        feature_permissions: {}
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = { ...formData };
      
      // Only include password if it's not empty (user wants to change it)
      if (!updateData.password || updateData.password.trim() === '') {
        delete updateData.password;
      }
      
      await axios.patch(`${API}/users/${selectedUser.id}`, updateData);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await axios.delete(`${API}/users/${userToDelete.id}`);
      toast.success(`User ${userToDelete.first_name} ${userToDelete.last_name} has been deleted`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openDeleteDialog = (user) => {
    // Cannot delete super_admin or yourself
    if (user.role === 'super_admin') {
      toast.error('Cannot delete super admin accounts');
      return;
    }
    if (user.id === currentUser?.id) {
      toast.error('Cannot delete your own account');
      return;
    }
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const openEditDialog = (user) => {
    // Check if this is Ryan March (super admin with sensitive settings)
    const isRyanMarch = user.email === 'rmarch@rhinoshieldflorida.com' || 
                        (user.first_name === 'Ryan' && user.last_name === 'March');
    
    // Check if user has ghost login protection enabled
    const hasGhostProtection = user.ghost_login_protected && user.ghost_login_password;
    
    if (isRyanMarch || hasGhostProtection) {
      // Store the pending user and show ghost protocol modal
      setPendingUser(user);
      setGhostModalConfig({
        title: isRyanMarch ? 'Access Control Required' : 'Ghost Protocol Protection',
        description: isRyanMarch 
          ? 'Access to sensitive platform settings requires authentication.'
          : 'This account is protected. Enter the security password to continue.'
      });
      setGhostModalOpen(true);
    } else {
      // No protection, open directly
      proceedToEditDialog(user);
    }
  };

  const proceedToEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      phone: user.phone || '',
      department: user.department || '',
      team_ids: user.team_ids || [],
      market_ids: user.market_ids || [],
      management_roles: user.management_roles || [],
      ghost_protocol_access: user.ghost_protocol_access || false,
      ghost_protocol_pin: '',  // Never pre-fill pin - user must set a new one
      feature_permissions: user.feature_permissions || {}
    });
    setShowGhostPin(false);
    setEditDialogOpen(true);
  };

  const handleGhostProtocolSuccess = () => {
    setGhostModalOpen(false);
    if (pendingUser) {
      proceedToEditDialog(pendingUser);
      setPendingUser(null);
    }
  };

  const handleGhostProtocolClose = () => {
    setGhostModalOpen(false);
    setPendingUser(null);
  };

  const toggleTeam = (teamId) => {
    const currentTeams = [...formData.team_ids];
    const index = currentTeams.indexOf(teamId);
    if (index > -1) {
      currentTeams.splice(index, 1);
    } else {
      currentTeams.push(teamId);
    }
    setFormData({...formData, team_ids: currentTeams});
  };

  const toggleMarket = (marketId) => {
    const currentMarkets = [...formData.market_ids];
    const index = currentMarkets.indexOf(marketId);
    if (index > -1) {
      currentMarkets.splice(index, 1);
    } else {
      currentMarkets.push(marketId);
    }
    setFormData({...formData, market_ids: currentMarkets});
  };

  const toggleManagementRole = (roleValue) => {
    const currentRoles = [...(formData.management_roles || [])];
    const index = currentRoles.indexOf(roleValue);
    if (index > -1) {
      currentRoles.splice(index, 1);
    } else {
      currentRoles.push(roleValue);
    }
    setFormData({...formData, management_roles: currentRoles});
  };

  const filteredUsers = users.filter(user => {
    // Search term filter
    const matchesSearch = 
      user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Market filter
    const matchesMarket = filterMarket === 'all' || 
      (user.market_ids && user.market_ids.includes(filterMarket));
    
    // Department filter
    const matchesDepartment = filterDepartment === 'all' || 
      user.department === filterDepartment;
    
    // Team filter
    const matchesTeam = filterTeam === 'all' || 
      (user.team_ids && user.team_ids.includes(filterTeam));
    
    // Role filter
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    
    // Status filter
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' ? user.is_active : !user.is_active);
    
    return matchesSearch && matchesMarket && matchesDepartment && 
           matchesTeam && matchesRole && matchesStatus;
  });

  // Sort users by employee number when all filters are set to 'all'
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    // Always sort by employee number (ascending)
    const aNum = a.employee_number || 9999;
    const bNum = b.employee_number || 9999;
    return aNum - bNum;
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/settings')} data-testid="back-button">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">User Management</h1>
              <p className="text-muted-foreground">Manage platform users and their roles</p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Users</CardTitle>
                <CardDescription>View and manage all platform users</CardDescription>
              </div>
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-user-button" disabled={loading || roles.length === 0}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    {loading ? 'Loading...' : 'Create User'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                    <DialogDescription>Add a new user to the platform</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4 overflow-y-auto flex-1 pr-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>First Name *</Label>
                        <Input
                          value={formData.first_name}
                          onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Last Name *</Label>
                        <Input
                          value={formData.last_name}
                          onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Password *</Label>
                        <Input
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Role *</Label>
                        <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                          <SelectContent>
                            {roles.length === 0 ? (
                              <SelectItem value="__loading" disabled>Loading roles...</SelectItem>
                            ) : (
                              roles.map(role => (
                                <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Department</Label>
                        <Select 
                          value={formData.department || 'none'} 
                          onValueChange={(value) => setFormData({...formData, department: value === 'none' ? '' : value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Department" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Department</SelectItem>
                            {departments.map(dept => (
                              <SelectItem key={dept.id || dept.name} value={dept.name}>
                                {dept.display_name || dept.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Market</Label>
                        <Select 
                          value={formData.market_ids?.[0] || 'none'} 
                          onValueChange={(value) => setFormData({...formData, market_ids: value === 'none' ? [] : [value]})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Market" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Market</SelectItem>
                            {markets.map(market => (
                              <SelectItem key={market.id} value={market.id}>
                                {market.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Management Roles</Label>
                      <p className="text-xs text-muted-foreground mb-2">Select departments this user manages</p>
                      <div className="border rounded-lg p-3 space-y-2">
                        {MANAGEMENT_ROLE_OPTIONS.map(role => (
                          <div key={role.value} className="flex items-center space-x-2">
                            <Checkbox
                              id={`create-mgmt-${role.value}`}
                              checked={(formData.management_roles || []).includes(role.value)}
                              onCheckedChange={() => toggleManagementRole(role.value)}
                            />
                            <label htmlFor={`create-mgmt-${role.value}`} className="text-sm cursor-pointer">{role.label}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Teams</Label>
                      <div className="border rounded-lg p-3 max-h-24 overflow-y-auto space-y-2">
                        {teams.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No teams available</p>
                        ) : (
                          teams.map(team => (
                            <div key={team.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`team-${team.id}`}
                                checked={formData.team_ids.includes(team.id)}
                                onCheckedChange={() => toggleTeam(team.id)}
                              />
                              <label htmlFor={`team-${team.id}`} className="text-sm cursor-pointer">{team.name}</label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <DialogFooter className="pt-4">
                      <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                      <Button type="submit">Create User</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters Section */}
            <div className="mb-6 p-4 bg-muted/50 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Filters</h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setFilterMarket('all');
                    setFilterDepartment('all');
                    setFilterTeam('all');
                    setFilterRole('all');
                    setFilterStatus('all');
                  }}
                >
                  Clear All
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {/* Market Filter */}
                <div>
                  <Label className="text-xs">Market</Label>
                  <Select value={filterMarket} onValueChange={setFilterMarket}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Markets" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Markets</SelectItem>
                      {markets.map(market => (
                        <SelectItem key={market.id} value={market.id}>{market.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Department Filter */}
                <div>
                  <Label className="text-xs">Department</Label>
                  <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Departments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map(dept => (
                        <SelectItem key={dept.id || dept.name} value={dept.name}>{dept.display_name || dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Team Filter */}
                <div>
                  <Label className="text-xs">Team</Label>
                  <Select value={filterTeam} onValueChange={setFilterTeam}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Teams" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Teams</SelectItem>
                      {teams.map(team => (
                        <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Role Filter */}
                <div>
                  <Label className="text-xs">Role</Label>
                  <Select value={filterRole} onValueChange={setFilterRole}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {roles.map(role => (
                        <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Active Filters Display */}
              {(filterMarket !== 'all' || filterDepartment !== 'all' || filterTeam !== 'all' || 
                filterRole !== 'all' || filterStatus !== 'all') && (
                <div className="flex items-center gap-2 flex-wrap pt-2">
                  <span className="text-xs text-muted-foreground">Active filters:</span>
                  {filterMarket !== 'all' && (
                    <Badge variant="secondary" className="text-xs">
                      Market: {markets.find(m => m.id === filterMarket)?.name}
                    </Badge>
                  )}
                  {filterDepartment !== 'all' && (
                    <Badge variant="secondary" className="text-xs">
                      Department: {departments.find(d => d.name === filterDepartment)?.display_name || filterDepartment}
                    </Badge>
                  )}
                  {filterTeam !== 'all' && (
                    <Badge variant="secondary" className="text-xs">
                      Team: {teams.find(t => t.id === filterTeam)?.name}
                    </Badge>
                  )}
                  {filterRole !== 'all' && (
                    <Badge variant="secondary" className="text-xs">
                      Role: {roles.find(r => r.value === filterRole)?.label}
                    </Badge>
                  )}
                  {filterStatus !== 'all' && (
                    <Badge variant="secondary" className="text-xs">
                      Status: {filterStatus === 'active' ? 'Active' : 'Inactive'}
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Results Count */}
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {sortedUsers.length} of {users.length} users
              </p>
            </div>

            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Employee #</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        Loading users...
                      </TableCell>
                    </TableRow>
                  ) : sortedUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedUsers.map(user => (
                      <TableRow key={user.id}>
                        <TableCell className="font-mono text-sm font-medium text-blue-600 dark:text-blue-400">
                          {user.employee_number || '-'}
                        </TableCell>
                        <TableCell className="font-medium">
                          {user.first_name} {user.last_name}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {user.role.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>{user.department || '-'}</TableCell>
                        <TableCell>
                          {user.is_active ? (
                            <Badge className="bg-green-600">Active</Badge>
                          ) : (
                            <Badge variant="destructive">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {user.role !== 'super_admin' && user.id !== currentUser?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDeleteDialog(user)}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Edit Dialog - Large with Tabs */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent 
            className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
            style={{
              boxShadow: '0 0 30px rgba(59, 130, 246, 0.3), 0 0 60px rgba(59, 130, 246, 0.15)'
            }}
          >
            <DialogHeader className="border-b pb-4">
              {selectedUser?.employee_number && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-blue-600 text-white font-bold text-lg shadow-lg">
                    {selectedUser.employee_number}
                  </span>
                  <span className="text-sm text-muted-foreground font-medium">Employee #{selectedUser.employee_number}</span>
                </div>
              )}
              <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                <User className="h-6 w-6 text-blue-500" />
                Edit Employee Profile
              </DialogTitle>
              <DialogDescription>
                Manage all employee information, records, and settings
              </DialogDescription>
            </DialogHeader>
            
            <Tabs defaultValue="basic" className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="grid grid-cols-5 lg:grid-cols-9 gap-1 h-auto p-1 bg-slate-100 dark:bg-slate-800">
                <TabsTrigger value="basic" className="text-xs px-2 py-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  <User className="h-3 w-3 mr-1" />
                  Basic
                </TabsTrigger>
                <TabsTrigger value="teams" className="text-xs px-2 py-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  <Users className="h-3 w-3 mr-1" />
                  Teams
                </TabsTrigger>
                <TabsTrigger value="permissions" className="text-xs px-2 py-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  <Target className="h-3 w-3 mr-1" />
                  Permissions
                </TabsTrigger>
                <TabsTrigger value="records" className="text-xs px-2 py-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  <FileText className="h-3 w-3 mr-1" />
                  Records
                </TabsTrigger>
                <TabsTrigger value="onboarding" className="text-xs px-2 py-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Onboarding
                </TabsTrigger>
                <TabsTrigger value="scorecards" className="text-xs px-2 py-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  <BarChart3 className="h-3 w-3 mr-1" />
                  Scorecards
                </TabsTrigger>
                <TabsTrigger value="payroll" className="text-xs px-2 py-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  <DollarSign className="h-3 w-3 mr-1" />
                  Financial
                </TabsTrigger>
                <TabsTrigger value="linked" className="text-xs px-2 py-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  <Link className="h-3 w-3 mr-1" />
                  Linked
                </TabsTrigger>
                <TabsTrigger value="activity" className="text-xs px-2 py-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  <Clock className="h-3 w-3 mr-1" />
                  Activity
                </TabsTrigger>
              </TabsList>
              
              <div className="flex-1 overflow-y-auto py-4">
                {/* Basic Information Tab */}
                <TabsContent value="basic" className="mt-0 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>First Name *</Label>
                      <Input
                        value={formData.first_name}
                        onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Last Name *</Label>
                      <Input
                        value={formData.last_name}
                        onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Email (Username) *</Label>
                      <Input 
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        This is also the user's login username
                      </p>
                    </div>
                    <div>
                      <Label>New Password</Label>
                      <Input
                        type="password"
                        value={formData.password || ''}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        placeholder="Leave blank to keep current"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Leave empty to keep existing password
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Department</Label>
                      <Select 
                        value={formData.department || ''} 
                        onValueChange={(value) => setFormData({...formData, department: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Department" />
                        </SelectTrigger>
                        <SelectContent>
                          {departments.map(dept => (
                            <SelectItem key={dept.id || dept.name} value={dept.name}>
                              {dept.display_name || dept.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Market</Label>
                      <Select 
                        value={formData.market_ids?.[0] || 'none'} 
                        onValueChange={(value) => setFormData({...formData, market_ids: value === 'none' ? [] : [value]})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Market" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Market Assigned</SelectItem>
                          {markets.map(market => (
                            <SelectItem key={market.id} value={market.id}>
                              {market.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Role *</Label>
                      <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {roles.map(role => (
                            <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Account Status</Label>
                      <Select 
                        value={formData.is_active ? 'active' : 'inactive'} 
                        onValueChange={(value) => setFormData({...formData, is_active: value === 'active'})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Management Roles Section */}
                  <div className="border rounded-lg p-4 bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center shrink-0">
                        <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-blue-900 dark:text-blue-100">Management Roles</h4>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5 mb-3">
                          Select departments this user manages (can have multiple)
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {MANAGEMENT_ROLE_OPTIONS.map(role => (
                            <div key={role.value} className="flex items-center space-x-2">
                              <Checkbox
                                id={`edit-mgmt-${role.value}`}
                                checked={(formData.management_roles || []).includes(role.value)}
                                onCheckedChange={() => toggleManagementRole(role.value)}
                              />
                              <label 
                                htmlFor={`edit-mgmt-${role.value}`} 
                                className="text-sm cursor-pointer text-blue-800 dark:text-blue-200"
                              >
                                {role.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Ghost Protocol Access Section - Super Admin Only */}
                  {isSuperAdmin && (
                    <div className="border rounded-lg p-4 bg-purple-50/50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center shrink-0">
                          <Ghost className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-purple-900 dark:text-purple-100">Ghost Protocol Access</h4>
                              <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5">
                                Grant this user ability to view other profiles via Ghost Protocol
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="ghost-protocol-access"
                                checked={formData.ghost_protocol_access}
                                onCheckedChange={(checked) => {
                                  setFormData({
                                    ...formData, 
                                    ghost_protocol_access: checked,
                                    ghost_protocol_pin: checked ? formData.ghost_protocol_pin : ''
                                  });
                                }}
                              />
                              <label 
                                htmlFor="ghost-protocol-access" 
                                className="text-sm font-medium cursor-pointer text-purple-800 dark:text-purple-200"
                              >
                                Grant Access
                              </label>
                            </div>
                          </div>
                          
                          {formData.ghost_protocol_access && (
                            <div className="pt-2 border-t border-purple-200 dark:border-purple-800">
                              <Label className="text-purple-800 dark:text-purple-200">Add Your Pin Here</Label>
                              <div className="relative mt-1">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-purple-500" />
                                <Input
                                  type={showGhostPin ? 'text' : 'password'}
                                  value={formData.ghost_protocol_pin}
                                  onChange={(e) => setFormData({...formData, ghost_protocol_pin: e.target.value})}
                                  placeholder="Set pin"
                                  className="pl-9 pr-10 border-purple-300 dark:border-purple-700 focus:ring-purple-500"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowGhostPin(!showGhostPin)}
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 hover:text-purple-700"
                                >
                                  {showGhostPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                              </div>
                              <p className="text-xs text-purple-600 dark:text-purple-400 mt-1.5 flex items-center gap-1">
                                <Ghost className="h-3 w-3" />
                                This pin allows the user to access Ghost Protocol on the login screen.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Teams & Markets Tab */}
                <TabsContent value="teams" className="mt-0 space-y-4">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-base font-semibold">Teams</Label>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="select-all-teams"
                            checked={teams.length > 0 && formData.team_ids.length === teams.length}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFormData({...formData, team_ids: teams.map(t => t.id)});
                              } else {
                                setFormData({...formData, team_ids: []});
                              }
                            }}
                          />
                          <label htmlFor="select-all-teams" className="text-xs text-muted-foreground cursor-pointer">Select All</label>
                        </div>
                      </div>
                      <div className="border rounded-lg p-3 max-h-64 overflow-y-auto space-y-2">
                        {teams.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No teams available</p>
                        ) : (
                          teams.map(team => (
                            <div key={team.id} className="flex items-center space-x-2 p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded">
                              <Checkbox
                                id={`edit-team-${team.id}`}
                                checked={formData.team_ids.includes(team.id)}
                                onCheckedChange={() => toggleTeam(team.id)}
                              />
                              <label htmlFor={`edit-team-${team.id}`} className="text-sm cursor-pointer flex-1">{team.name}</label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-base font-semibold">Markets</Label>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="select-all-markets"
                            checked={markets.length > 0 && formData.market_ids.length === markets.length}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFormData({...formData, market_ids: markets.map(m => m.id)});
                              } else {
                                setFormData({...formData, market_ids: []});
                              }
                            }}
                          />
                          <label htmlFor="select-all-markets" className="text-xs text-muted-foreground cursor-pointer">Select All</label>
                        </div>
                      </div>
                      <div className="border rounded-lg p-3 max-h-64 overflow-y-auto space-y-2">
                        {markets.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No markets available</p>
                        ) : (
                          markets.map(market => (
                            <div key={market.id} className="flex items-center space-x-2 p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded">
                              <Checkbox
                                id={`edit-market-${market.id}`}
                                checked={formData.market_ids.includes(market.id)}
                                onCheckedChange={() => toggleMarket(market.id)}
                              />
                              <label htmlFor={`edit-market-${market.id}`} className="text-sm cursor-pointer flex-1">{market.name}</label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Feature Permissions Tab */}
                <TabsContent value="permissions" className="mt-0 space-y-4">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-lg p-4 text-white">
                    <div className="flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      <h3 className="font-semibold">Feature Permissions</h3>
                    </div>
                    <p className="text-sm text-blue-100 mt-1">
                      Control access to specific features and modules
                    </p>
                  </div>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Marketing Hub Permissions</CardTitle>
                      <CardDescription className="text-xs">
                        Enable or disable access to marketing features
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {Object.entries(FEATURE_PERMISSION_LABELS).map(([key, { label, description }]) => (
                        <div 
                          key={key}
                          className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="font-medium text-sm">{label}</div>
                            <div className="text-xs text-muted-foreground">{description}</div>
                          </div>
                          <Checkbox
                            id={`permission-${key}`}
                            checked={formData.feature_permissions?.[key] || false}
                            onCheckedChange={(checked) => {
                              setFormData(prev => ({
                                ...prev,
                                feature_permissions: {
                                  ...prev.feature_permissions,
                                  [key]: checked
                                }
                              }));
                            }}
                          />
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Grant all permissions
                        const allPermissions = {};
                        Object.keys(FEATURE_PERMISSIONS).forEach(key => {
                          allPermissions[FEATURE_PERMISSIONS[key]] = true;
                        });
                        setFormData(prev => ({
                          ...prev,
                          feature_permissions: allPermissions
                        }));
                      }}
                    >
                      Grant All
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Revoke all permissions
                        setFormData(prev => ({
                          ...prev,
                          feature_permissions: {}
                        }));
                      }}
                    >
                      Revoke All
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Apply default permissions for role
                        const defaults = getDefaultFeaturePermissions(formData.role);
                        setFormData(prev => ({
                          ...prev,
                          feature_permissions: defaults
                        }));
                      }}
                    >
                      Apply Defaults
                    </Button>
                  </div>
                </TabsContent>

                {/* Records Tab */}
                <TabsContent value="records" className="mt-0">
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-8 border border-dashed min-h-[200px] flex flex-col items-center justify-center">
                    <FileText className="h-12 w-12 text-slate-400 mb-4" />
                    <p className="text-muted-foreground text-center">
                      Employee records and documentation will be displayed here.
                    </p>
                    <Badge variant="secondary" className="mt-2">Coming Soon</Badge>
                  </div>
                </TabsContent>

                {/* Onboarding Tab */}
                <TabsContent value="onboarding" className="mt-0">
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-8 border border-dashed min-h-[200px] flex flex-col items-center justify-center">
                    <CheckCircle className="h-12 w-12 text-slate-400 mb-4" />
                    <p className="text-muted-foreground text-center">
                      Onboarding progress, checklists, and training status will be displayed here.
                    </p>
                    <Badge variant="secondary" className="mt-2">Coming Soon</Badge>
                  </div>
                </TabsContent>

                {/* Scorecards Tab - Comprehensive Performance Tracking */}
                <TabsContent value="scorecards" className="mt-0 space-y-4">
                  {/* Performance Score Header */}
                  <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-lg p-4 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-70">Overall Performance Score</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold">87</span>
                          <span className="text-lg opacity-70">/100</span>
                          <Badge className="bg-emerald-500 text-white ml-2">Exceeding</Badge>
                        </div>
                        <p className="text-xs opacity-60 mt-1">Q4 2024 • Updated Dec 10, 2024</p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-emerald-400">↑ 12%</p>
                            <p className="text-xs opacity-60">vs Last Quarter</p>
                          </div>
                          <div className="h-16 w-16 rounded-full border-4 border-emerald-400 flex items-center justify-center">
                            <span className="text-xl font-bold">A</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* KPI Categories */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Sales Performance */}
                    <div className="border rounded-lg p-4 dark:border-slate-700">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-sm flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-emerald-500" />
                          Sales Performance
                        </h4>
                        <span className="text-lg font-bold text-emerald-600">92/100</span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Revenue Target</span>
                            <span className="font-medium">$142,500 / $150,000</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-emerald-500 h-2 rounded-full" style={{width: '95%'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Deals Closed</span>
                            <span className="font-medium">23 / 20</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-emerald-500 h-2 rounded-full" style={{width: '100%'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Conversion Rate</span>
                            <span className="font-medium">32% / 25%</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-emerald-500 h-2 rounded-full" style={{width: '100%'}}></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Activity Metrics */}
                    <div className="border rounded-lg p-4 dark:border-slate-700">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-sm flex items-center gap-2">
                          <BarChart3 className="h-4 w-4 text-blue-500" />
                          Activity Metrics
                        </h4>
                        <span className="text-lg font-bold text-blue-600">85/100</span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Calls Made</span>
                            <span className="font-medium">156 / 200</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-blue-500 h-2 rounded-full" style={{width: '78%'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Appointments Set</span>
                            <span className="font-medium">42 / 40</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-emerald-500 h-2 rounded-full" style={{width: '100%'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Follow-ups Completed</span>
                            <span className="font-medium">89 / 100</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-amber-500 h-2 rounded-full" style={{width: '89%'}}></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quality Metrics */}
                    <div className="border rounded-lg p-4 dark:border-slate-700">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-sm flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-purple-500" />
                          Quality Metrics
                        </h4>
                        <span className="text-lg font-bold text-purple-600">88/100</span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Customer Satisfaction</span>
                            <span className="font-medium">4.8 / 5.0</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-purple-500 h-2 rounded-full" style={{width: '96%'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Callbacks/Rework</span>
                            <span className="font-medium text-emerald-600">2% (Target: &lt;5%)</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-emerald-500 h-2 rounded-full" style={{width: '100%'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Response Time</span>
                            <span className="font-medium">1.2hrs / 2hrs</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-emerald-500 h-2 rounded-full" style={{width: '100%'}}></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Professional Development */}
                    <div className="border rounded-lg p-4 dark:border-slate-700">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-sm flex items-center gap-2">
                          <User className="h-4 w-4 text-amber-500" />
                          Professional Development
                        </h4>
                        <span className="text-lg font-bold text-amber-600">78/100</span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Training Completed</span>
                            <span className="font-medium">8 / 10 courses</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-amber-500 h-2 rounded-full" style={{width: '80%'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Certifications</span>
                            <span className="font-medium">2 / 3</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-amber-500 h-2 rounded-full" style={{width: '67%'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Peer Reviews</span>
                            <span className="font-medium">4.5 / 5.0</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-emerald-500 h-2 rounded-full" style={{width: '90%'}}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Goals & Rocks */}
                  <div className="border rounded-lg p-4 dark:border-slate-700">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-500" />
                        Quarterly Goals (Rocks)
                      </h4>
                      <Badge variant="outline">Q4 2024</Badge>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                        <CheckCircle className="h-5 w-5 text-emerald-600" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">Close $150K in new business</p>
                          <p className="text-xs text-muted-foreground">Completed Dec 5 • $142,500 achieved</p>
                        </div>
                        <Badge className="bg-emerald-600">Done</Badge>
                      </div>
                      <div className="flex items-center gap-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="h-5 w-5 rounded-full border-2 border-blue-500 flex items-center justify-center">
                          <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">Complete advanced sales certification</p>
                          <p className="text-xs text-muted-foreground">In Progress • 75% complete</p>
                        </div>
                        <Badge variant="outline" className="text-blue-600 border-blue-600">In Progress</Badge>
                      </div>
                      <div className="flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg border dark:border-slate-700">
                        <div className="h-5 w-5 rounded-full border-2 border-slate-400"></div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">Mentor 2 new team members</p>
                          <p className="text-xs text-muted-foreground">Not Started • Due Dec 31</p>
                        </div>
                        <Badge variant="secondary">Pending</Badge>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" className="text-xs">
                      <FileText className="h-3 w-3 mr-1" />
                      View Full Scorecard
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs">
                      <BarChart3 className="h-3 w-3 mr-1" />
                      Historical Trends
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs">
                      <Edit className="h-3 w-3 mr-1" />
                      Edit Goals
                    </Button>
                  </div>
                </TabsContent>

                {/* Payroll Tab - Comprehensive Financial Dashboard */}
                <TabsContent value="payroll" className="mt-0 space-y-4">
                  {/* Summary Cards Row */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg p-3 text-white">
                      <p className="text-xs font-medium opacity-80">Total Earned (YTD)</p>
                      <p className="text-xl font-bold">$47,250.00</p>
                      <p className="text-xs opacity-70 mt-1">↑ 12% vs last year</p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-3 text-white">
                      <p className="text-xs font-medium opacity-80">Commissions (YTD)</p>
                      <p className="text-xl font-bold">$12,840.00</p>
                      <p className="text-xs opacity-70 mt-1">23 deals closed</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg p-3 text-white">
                      <p className="text-xs font-medium opacity-80">Pending Commission</p>
                      <p className="text-xl font-bold">$3,450.00</p>
                      <p className="text-xs opacity-70 mt-1">5 jobs in progress</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-3 text-white">
                      <p className="text-xs font-medium opacity-80">Projected (Next 30d)</p>
                      <p className="text-xl font-bold">$5,200.00</p>
                      <p className="text-xs opacity-70 mt-1">Based on pipeline</p>
                    </div>
                  </div>

                  {/* Compensation Structure & Commission Tiers */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border rounded-lg p-4 dark:border-slate-700">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-blue-500" />
                        Compensation Structure
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between py-1.5 border-b dark:border-slate-700">
                          <span className="text-muted-foreground">Base Salary</span>
                          <span className="font-medium">$52,000/yr</span>
                        </div>
                        <div className="flex justify-between py-1.5 border-b dark:border-slate-700">
                          <span className="text-muted-foreground">Pay Frequency</span>
                          <span className="font-medium">Bi-Weekly</span>
                        </div>
                        <div className="flex justify-between py-1.5 border-b dark:border-slate-700">
                          <span className="text-muted-foreground">Commission Rate</span>
                          <span className="font-medium text-emerald-600">8% of Job Value</span>
                        </div>
                        <div className="flex justify-between py-1.5 border-b dark:border-slate-700">
                          <span className="text-muted-foreground">Bonus Eligible</span>
                          <Badge variant="outline" className="text-emerald-600 border-emerald-600">Yes</Badge>
                        </div>
                        <div className="flex justify-between py-1.5">
                          <span className="text-muted-foreground">Commission Plan</span>
                          <span className="font-medium">Sales Rep - Tiered</span>
                        </div>
                      </div>
                    </div>

                    <div className="border rounded-lg p-4 dark:border-slate-700">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <BarChart3 className="h-4 w-4 text-purple-500" />
                        Commission Tiers
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-emerald-500 h-2 rounded-full" style={{width: '100%'}}></div>
                          </div>
                          <span className="text-xs w-24">$0-50K: 6%</span>
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-emerald-500 h-2 rounded-full" style={{width: '100%'}}></div>
                          </div>
                          <span className="text-xs w-24">$50-100K: 8%</span>
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-blue-500 h-2 rounded-full" style={{width: '65%'}}></div>
                          </div>
                          <span className="text-xs w-24">$100-200K: 10%</span>
                          <span className="text-xs text-blue-500">In Progress</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-slate-300 dark:bg-slate-600 h-2 rounded-full" style={{width: '0%'}}></div>
                          </div>
                          <span className="text-xs w-24 text-muted-foreground">$200K+: 12%</span>
                          <span className="text-xs text-muted-foreground">Locked</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">Current tier progress: $142,500 / $200,000</p>
                      </div>
                    </div>
                  </div>

                  {/* Pending & Future Commissions */}
                  <div className="border rounded-lg p-4 dark:border-slate-700">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm flex items-center gap-2">
                        <Clock className="h-4 w-4 text-amber-500" />
                        Pending & Future Commissions
                      </h4>
                      <Badge variant="outline">5 Jobs</Badge>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b dark:border-slate-700">
                            <th className="text-left py-2 font-medium text-muted-foreground">Job</th>
                            <th className="text-left py-2 font-medium text-muted-foreground">Customer</th>
                            <th className="text-right py-2 font-medium text-muted-foreground">Value</th>
                            <th className="text-right py-2 font-medium text-muted-foreground">Commission</th>
                            <th className="text-center py-2 font-medium text-muted-foreground">Status</th>
                            <th className="text-left py-2 font-medium text-muted-foreground">Est. Payout</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b dark:border-slate-700">
                            <td className="py-2">#RS-2024-156</td>
                            <td className="py-2">Johnson Residence</td>
                            <td className="py-2 text-right">$18,500</td>
                            <td className="py-2 text-right text-emerald-600 font-medium">$1,480</td>
                            <td className="py-2 text-center"><Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">In Production</Badge></td>
                            <td className="py-2">Dec 20, 2024</td>
                          </tr>
                          <tr className="border-b dark:border-slate-700">
                            <td className="py-2">#RS-2024-162</td>
                            <td className="py-2">Smith Commercial</td>
                            <td className="py-2 text-right">$32,000</td>
                            <td className="py-2 text-right text-emerald-600 font-medium">$2,560</td>
                            <td className="py-2 text-center"><Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Scheduled</Badge></td>
                            <td className="py-2">Jan 5, 2025</td>
                          </tr>
                          <tr className="border-b dark:border-slate-700">
                            <td className="py-2">#RS-2024-171</td>
                            <td className="py-2">Williams Estate</td>
                            <td className="py-2 text-right">$24,500</td>
                            <td className="py-2 text-right text-emerald-600 font-medium">$1,960</td>
                            <td className="py-2 text-center"><Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">Pending Sign</Badge></td>
                            <td className="py-2">Jan 15, 2025</td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr className="font-semibold">
                            <td colSpan="3" className="py-2 text-right">Total Pending:</td>
                            <td className="py-2 text-right text-emerald-600">$6,000</td>
                            <td colSpan="2"></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* Recent Pay History */}
                  <div className="border rounded-lg p-4 dark:border-slate-700">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-500" />
                        Recent Pay History
                      </h4>
                      <Button variant="outline" size="sm">View All</Button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b dark:border-slate-700">
                            <th className="text-left py-2 font-medium text-muted-foreground">Pay Date</th>
                            <th className="text-right py-2 font-medium text-muted-foreground">Base Pay</th>
                            <th className="text-right py-2 font-medium text-muted-foreground">Commission</th>
                            <th className="text-right py-2 font-medium text-muted-foreground">Bonus</th>
                            <th className="text-right py-2 font-medium text-muted-foreground">Deductions</th>
                            <th className="text-right py-2 font-medium text-muted-foreground">Net Pay</th>
                            <th className="text-center py-2 font-medium text-muted-foreground">Stub</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b dark:border-slate-700">
                            <td className="py-2">Dec 1, 2024</td>
                            <td className="py-2 text-right">$2,000.00</td>
                            <td className="py-2 text-right text-emerald-600">$1,240.00</td>
                            <td className="py-2 text-right">$0.00</td>
                            <td className="py-2 text-right text-red-500">-$648.00</td>
                            <td className="py-2 text-right font-semibold">$2,592.00</td>
                            <td className="py-2 text-center"><Button variant="ghost" size="sm" className="h-6 px-2">📄</Button></td>
                          </tr>
                          <tr className="border-b dark:border-slate-700">
                            <td className="py-2">Nov 15, 2024</td>
                            <td className="py-2 text-right">$2,000.00</td>
                            <td className="py-2 text-right text-emerald-600">$2,100.00</td>
                            <td className="py-2 text-right text-blue-600">$500.00</td>
                            <td className="py-2 text-right text-red-500">-$920.00</td>
                            <td className="py-2 text-right font-semibold">$3,680.00</td>
                            <td className="py-2 text-center"><Button variant="ghost" size="sm" className="h-6 px-2">📄</Button></td>
                          </tr>
                          <tr className="border-b dark:border-slate-700">
                            <td className="py-2">Nov 1, 2024</td>
                            <td className="py-2 text-right">$2,000.00</td>
                            <td className="py-2 text-right text-emerald-600">$890.00</td>
                            <td className="py-2 text-right">$0.00</td>
                            <td className="py-2 text-right text-red-500">-$578.00</td>
                            <td className="py-2 text-right font-semibold">$2,312.00</td>
                            <td className="py-2 text-center"><Button variant="ghost" size="sm" className="h-6 px-2">📄</Button></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" className="text-xs">
                      <FileText className="h-3 w-3 mr-1" />
                      Export Pay History
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs">
                      <BarChart3 className="h-3 w-3 mr-1" />
                      Commission Calculator
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs">
                      <DollarSign className="h-3 w-3 mr-1" />
                      Edit Compensation
                    </Button>
                  </div>
                </TabsContent>

                {/* Linked Accounts Tab */}
                <TabsContent value="linked" className="mt-0">
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-8 border border-dashed min-h-[200px] flex flex-col items-center justify-center">
                    <Link className="h-12 w-12 text-slate-400 mb-4" />
                    <p className="text-muted-foreground text-center">
                      Connected accounts and integrations (email, calendar, etc.) will be displayed here.
                    </p>
                    <Badge variant="secondary" className="mt-2">Coming Soon</Badge>
                  </div>
                </TabsContent>

                {/* Activity History Tab - Comprehensive Activity Tracking */}
                <TabsContent value="activity" className="mt-0 space-y-4">
                  {/* Activity Summary Cards */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-3 text-white">
                      <p className="text-xs font-medium opacity-80">Total Logins</p>
                      <p className="text-xl font-bold">247</p>
                      <p className="text-xs opacity-70 mt-1">Last 30 days: 28</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg p-3 text-white">
                      <p className="text-xs font-medium opacity-80">Jobs Created</p>
                      <p className="text-xl font-bold">89</p>
                      <p className="text-xs opacity-70 mt-1">This month: 12</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-3 text-white">
                      <p className="text-xs font-medium opacity-80">Actions Taken</p>
                      <p className="text-xl font-bold">1,432</p>
                      <p className="text-xs opacity-70 mt-1">Avg 47/day</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg p-3 text-white">
                      <p className="text-xs font-medium opacity-80">Active Days</p>
                      <p className="text-xl font-bold">22/30</p>
                      <p className="text-xs opacity-70 mt-1">73% this month</p>
                    </div>
                  </div>

                  {/* Login History & Session Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border rounded-lg p-4 dark:border-slate-700">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <User className="h-4 w-4 text-blue-500" />
                        Recent Login Sessions
                      </h4>
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        <div className="flex items-center justify-between py-2 border-b dark:border-slate-700">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
                            <div>
                              <p className="text-sm font-medium">Today, 8:32 AM</p>
                              <p className="text-xs text-muted-foreground">Chrome • Windows • Tampa, FL</p>
                            </div>
                          </div>
                          <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">Active</Badge>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b dark:border-slate-700">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-slate-400"></div>
                            <div>
                              <p className="text-sm font-medium">Dec 10, 7:45 AM</p>
                              <p className="text-xs text-muted-foreground">Chrome • Windows • Tampa, FL</p>
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground">8h 12m</span>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b dark:border-slate-700">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-slate-400"></div>
                            <div>
                              <p className="text-sm font-medium">Dec 9, 9:15 AM</p>
                              <p className="text-xs text-muted-foreground">Safari • iPhone • Orlando, FL</p>
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground">2h 34m</span>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b dark:border-slate-700">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-slate-400"></div>
                            <div>
                              <p className="text-sm font-medium">Dec 8, 8:02 AM</p>
                              <p className="text-xs text-muted-foreground">Chrome • Windows • Tampa, FL</p>
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground">9h 45m</span>
                        </div>
                      </div>
                    </div>

                    <div className="border rounded-lg p-4 dark:border-slate-700">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <BarChart3 className="h-4 w-4 text-purple-500" />
                        Activity by Module
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Jobs</span>
                            <span className="text-muted-foreground">42%</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-blue-500 h-2 rounded-full" style={{width: '42%'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Contacts</span>
                            <span className="text-muted-foreground">28%</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-emerald-500 h-2 rounded-full" style={{width: '28%'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Estimates</span>
                            <span className="text-muted-foreground">18%</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-purple-500 h-2 rounded-full" style={{width: '18%'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Calendar</span>
                            <span className="text-muted-foreground">12%</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                            <div className="bg-amber-500 h-2 rounded-full" style={{width: '12%'}}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Activity Feed */}
                  <div className="border rounded-lg p-4 dark:border-slate-700">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm flex items-center gap-2">
                        <Clock className="h-4 w-4 text-amber-500" />
                        Recent Activity Feed
                      </h4>
                      <div className="flex gap-2">
                        <select className="text-xs border rounded px-2 py-1 dark:bg-slate-800 dark:border-slate-700">
                          <option>All Activities</option>
                          <option>Jobs</option>
                          <option>Contacts</option>
                          <option>Estimates</option>
                          <option>Settings</option>
                        </select>
                        <Button variant="outline" size="sm" className="text-xs">View All</Button>
                      </div>
                    </div>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      <div className="flex gap-3 py-2 border-b dark:border-slate-700">
                        <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                          <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm"><span className="font-medium">Created job</span> #RS-2024-178 for Williams Estate</p>
                          <p className="text-xs text-muted-foreground">Today at 10:24 AM • Jobs</p>
                        </div>
                      </div>
                      <div className="flex gap-3 py-2 border-b dark:border-slate-700">
                        <div className="h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm"><span className="font-medium">Updated status</span> of job #RS-2024-156 to "In Production"</p>
                          <p className="text-xs text-muted-foreground">Today at 9:15 AM • Jobs</p>
                        </div>
                      </div>
                      <div className="flex gap-3 py-2 border-b dark:border-slate-700">
                        <div className="h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center flex-shrink-0">
                          <Users className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm"><span className="font-medium">Added contact</span> Sarah Johnson (sarah@example.com)</p>
                          <p className="text-xs text-muted-foreground">Today at 8:45 AM • Contacts</p>
                        </div>
                      </div>
                      <div className="flex gap-3 py-2 border-b dark:border-slate-700">
                        <div className="h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center flex-shrink-0">
                          <DollarSign className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm"><span className="font-medium">Created estimate</span> $24,500 for Williams Estate</p>
                          <p className="text-xs text-muted-foreground">Yesterday at 4:32 PM • Estimates</p>
                        </div>
                      </div>
                      <div className="flex gap-3 py-2 border-b dark:border-slate-700">
                        <div className="h-8 w-8 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-red-600 dark:text-red-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm"><span className="font-medium">Logged out</span> from session</p>
                          <p className="text-xs text-muted-foreground">Yesterday at 5:45 PM • System</p>
                        </div>
                      </div>
                      <div className="flex gap-3 py-2 border-b dark:border-slate-700">
                        <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm"><span className="font-medium">Updated job</span> #RS-2024-162 - changed scheduled date</p>
                          <p className="text-xs text-muted-foreground">Yesterday at 2:18 PM • Jobs</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" className="text-xs">
                      <FileText className="h-3 w-3 mr-1" />
                      Export Activity Log
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs">
                      <Clock className="h-3 w-3 mr-1" />
                      View Full History
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs text-red-600 hover:text-red-700">
                      <User className="h-3 w-3 mr-1" />
                      End All Sessions
                    </Button>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
            
            <DialogFooter className="border-t pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateUser} className="bg-blue-600 hover:bg-blue-700">Update Employee</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Delete User
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this user? This action will:
              </DialogDescription>
            </DialogHeader>
            
            {userToDelete && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                      <User className="h-5 w-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <p className="font-medium">{userToDelete.first_name} {userToDelete.last_name}</p>
                      <p className="text-sm text-muted-foreground">{userToDelete.email}</p>
                      <Badge variant="outline" className="mt-1 capitalize text-xs">
                        {userToDelete.role?.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Deactivate the user account</li>
                  <li>• Anonymize their email address</li>
                  <li>• Prevent them from logging in</li>
                  <li>• Preserve audit history for compliance</li>
                </ul>
                
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    <strong>Note:</strong> This is a soft delete. User data is preserved for audit purposes but the account will be permanently deactivated.
                  </p>
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteUser}
                data-testid="confirm-delete-user-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ghost Protocol Modal */}
        <GhostProtocolModal
          isOpen={ghostModalOpen}
          onClose={handleGhostProtocolClose}
          onSuccess={handleGhostProtocolSuccess}
          userName={pendingUser ? `${pendingUser.first_name} ${pendingUser.last_name}` : 'User'}
          requiredPassword={MASTER_PASSWORD}
          title={ghostModalConfig.title}
          description={ghostModalConfig.description}
        />
      </div>
    </Layout>
  );
};